import { useEffect, useMemo, useRef, useState } from 'react';
import type { LapRef, MapColourMode, SessionData } from '@tla/types';
import { getChannel, interpolateNaN, projectLocal } from '@tla/core';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useCursorStore } from '../../store/cursorStore';
import { usePlaybackStore } from '../../store/playbackStore';
import { useLayoutStore } from '../../store/layoutStore';
import { loadSession } from '../../session/sessionCache';
import { lapColour, lapKey } from '../../store/colours';
import { channelForMode, colourFor } from './colourMap';
import {
  centroidOf,
  drawGateLine,
  drawSectorGates,
  nearestByDistance,
  outwardLabelPos,
  perpendicularGate,
  satelliteUrl,
  sectorCountForTrack,
} from './trackDraw';
import { TrackEditor } from '../TrackEditor';

interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** One projected racing line (reference or a comparison lap). */
interface LapPath {
  /** Stable lapKey for hidden-state lookups. */
  key: string;
  /** Projected local coordinates [x,y] per sample. */
  pts: [number, number][];
  /** Colour channel value per sample (accel/brake/speed). */
  colourValues: Float64Array;
  /** Distance from lap start (metres) per sample. */
  distance: Float64Array;
  /** Elapsed seconds from lap start per sample (for realtime playback). */
  time: Float64Array;
  /** Identity outline colour for this lap. */
  outline: string;
  /** Total lap distance in metres. */
  lapDistance: number;
}

interface MapData {
  /** Reference lap first, then comparison laps. */
  laps: LapPath[];
  /** Combined projected bounds across all laps (with margin). */
  bounds: Bounds;
  /** Geographic bounds [lon/lat] matching {@link bounds}, for satellite fetch. */
  geo: { minLon: number; maxLon: number; minLat: number; maxLat: number };
  /**
   * Projected bounds the satellite imagery is drawn across. Derived from the
   * track's cached geographic extent (see {@link satGeo}) reprojected onto the
   * current frame, so adding/removing comparison laps never shifts the imagery.
   */
  satBounds: Bounds;
  /**
   * Geographic extent the satellite imagery covers. Fixed per track (cached on
   * first load) so comparing laps reuses the same imagery instead of refetching.
   */
  satGeo: { minLon: number; maxLon: number; minLat: number; maxLat: number };
  /** Combined colour value range across all laps. */
  range: [number, number];
  /** Projection centre [lon, lat] the shared local frame is built around. */
  centre: [number, number];
}

/** Current pan/zoom applied on top of the base fit. */
interface View {
  scale: number;
  tx: number;
  ty: number;
}

interface RawLap {
  path: LapPath;
  bounds: Bounds;
  geo: { minLon: number; maxLon: number; minLat: number; maxLat: number };
  range: [number, number];
}

/** Project a single lap's racing line onto a shared local frame. */
function projectLap(
  session: SessionData,
  lapNumber: number,
  centre: [number, number],
  mode: MapColourMode,
  outline: string,
  key: string,
): RawLap | null {
  const lap = session.laps.find((l) => l.number === lapNumber);
  const lat = getChannel(session, 'latitude');
  const lon = getChannel(session, 'longitude');
  if (!lap || !lat || !lon) return null;

  const { startIdx, endIdx } = lap;
  const latV = interpolateNaN(lat.samples.slice(startIdx, endIdx + 1));
  const lonV = interpolateNaN(lon.samples.slice(startIdx, endIdx + 1));
  const n = latV.length;

  const pts: [number, number][] = new Array(n);
  const bounds: Bounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  const geo = { minLon: Infinity, maxLon: -Infinity, minLat: Infinity, maxLat: -Infinity };
  for (let i = 0; i < n; i++) {
    const [x, y] = projectLocal([lonV[i], latV[i]], centre);
    pts[i] = [x, y];
    if (x < bounds.minX) bounds.minX = x;
    if (x > bounds.maxX) bounds.maxX = x;
    if (y < bounds.minY) bounds.minY = y;
    if (y > bounds.maxY) bounds.maxY = y;
    if (lonV[i] < geo.minLon) geo.minLon = lonV[i];
    if (lonV[i] > geo.maxLon) geo.maxLon = lonV[i];
    if (latV[i] < geo.minLat) geo.minLat = latV[i];
    if (latV[i] > geo.maxLat) geo.maxLat = latV[i];
  }

  const colourCh = getChannel(session, channelForMode(mode));
  const colourValues = colourCh
    ? interpolateNaN(colourCh.samples.slice(startIdx, endIdx + 1))
    : new Float64Array(n);

  const distance = new Float64Array(n);
  const dStart = session.distance[startIdx];
  for (let i = 0; i < n; i++) distance[i] = session.distance[startIdx + i] - dStart;

  const time = new Float64Array(n);
  const tStart = session.time[startIdx];
  for (let i = 0; i < n; i++) time[i] = session.time[startIdx + i] - tStart;

  let lo = Infinity;
  let hi = -Infinity;
  for (const v of colourValues) {
    if (!Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }

  return {
    path: { key, pts, colourValues, distance, time, outline, lapDistance: distance[n - 1] },
    bounds,
    geo,
    range: [lo, hi],
  };
}

/**
 * Build the reference lap plus any comparison laps on a shared projection frame
 * (centred on the reference lap) so the racing lines overlay correctly.
 */
function buildMap(
  refSession: SessionData,
  refLap: number,
  refKey: string,
  comparisons: { session: SessionData; lapNumber: number; colour: string; key: string }[],
  mode: MapColourMode,
  trackId: string | null,
): MapData | null {
  const lap = refSession.laps.find((l) => l.number === refLap);
  const lat = getChannel(refSession, 'latitude');
  const lon = getChannel(refSession, 'longitude');
  if (!lap || !lat || !lon) return null;

  // Centre the shared projection on the reference lap centroid.
  const latV = interpolateNaN(lat.samples.slice(lap.startIdx, lap.endIdx + 1));
  const lonV = interpolateNaN(lon.samples.slice(lap.startIdx, lap.endIdx + 1));
  let cLon = 0;
  let cLat = 0;
  for (let i = 0; i < latV.length; i++) {
    cLon += lonV[i];
    cLat += latV[i];
  }
  const centre: [number, number] = [cLon / latV.length, cLat / latV.length];

  const raws: RawLap[] = [];
  const ref = projectLap(refSession, refLap, centre, mode, lapColour(0), refKey);
  if (!ref) return null;
  raws.push(ref);
  comparisons.forEach((c) => {
    const r = projectLap(c.session, c.lapNumber, centre, mode, c.colour, c.key);
    if (r) raws.push(r);
  });

  // Combine bounds, geo bounds and colour range across every lap.
  const bounds: Bounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  const geo = { minLon: Infinity, maxLon: -Infinity, minLat: Infinity, maxLat: -Infinity };
  let lo = Infinity;
  let hi = -Infinity;
  for (const r of raws) {
    bounds.minX = Math.min(bounds.minX, r.bounds.minX);
    bounds.maxX = Math.max(bounds.maxX, r.bounds.maxX);
    bounds.minY = Math.min(bounds.minY, r.bounds.minY);
    bounds.maxY = Math.max(bounds.maxY, r.bounds.maxY);
    geo.minLon = Math.min(geo.minLon, r.geo.minLon);
    geo.maxLon = Math.max(geo.maxLon, r.geo.maxLon);
    geo.minLat = Math.min(geo.minLat, r.geo.minLat);
    geo.maxLat = Math.max(geo.maxLat, r.geo.maxLat);
    lo = Math.min(lo, r.range[0]);
    hi = Math.max(hi, r.range[1]);
  }

  // Matching fractional margin on both projected and geographic bounds keeps
  // the satellite image aligned with the racing line.
  const m = 0.06;
  const mx = (bounds.maxX - bounds.minX) * m;
  const my = (bounds.maxY - bounds.minY) * m;
  bounds.minX -= mx;
  bounds.maxX += mx;
  bounds.minY -= my;
  bounds.maxY += my;
  const mLon = (geo.maxLon - geo.minLon) * m;
  const mLat = (geo.maxLat - geo.minLat) * m;
  geo.minLon -= mLon;
  geo.maxLon += mLon;
  geo.minLat -= mLat;
  geo.maxLat += mLat;

  // Satellite extent: fixed per track. On the first load for a track we derive
  // it from the reference lap (which covers the whole circuit) and cache it;
  // thereafter every lap on that track reuses the same geographic extent, so
  // adding comparison laps or switching the reference lap never refetches or
  // shifts the imagery. The cached geo extent is reprojected onto the current
  // frame each build so it stays aligned with the racing lines.
  const cached = trackId ? satCache.get(trackId) : undefined;
  const satGeo = cached
    ? { ...cached.geo }
    : (() => {
        const g = { ...ref.geo };
        const gl = (g.maxLon - g.minLon) * m;
        const ga = (g.maxLat - g.minLat) * m;
        g.minLon -= gl;
        g.maxLon += gl;
        g.minLat -= ga;
        g.maxLat += ga;
        return g;
      })();
  const [sxMin, syMin] = projectLocal([satGeo.minLon, satGeo.minLat], centre);
  const [sxMax, syMax] = projectLocal([satGeo.maxLon, satGeo.maxLat], centre);
  const satBounds: Bounds = {
    minX: Math.min(sxMin, sxMax),
    maxX: Math.max(sxMin, sxMax),
    minY: Math.min(syMin, syMax),
    maxY: Math.max(syMin, syMax),
  };

  return { laps: raws.map((r) => r.path), bounds, geo, satBounds, satGeo, range: [lo, hi], centre };
}


/**
 * Deepest imagery pyramid level to build. Level `L` splits the track bbox into
 * a 2^L × 2^L grid of tiles, each fetched at near-max provider resolution, so
 * the effective resolution across the whole track is (base × 2^L). Level 2
 * (a 4×4 grid) is ~4× sharper than a single image without an excessive number
 * of tile requests.
 */
const SAT_MAX_LEVEL = 3;

/** A single satellite tile: its fractional bbox within the track bounds. */
interface SatTile {
  /** Longitude fractions [0..1] within the track bbox (west→east). */
  u0: number;
  u1: number;
  /** Latitude fractions [0..1] within the track bbox (south→north). */
  v0: number;
  v1: number;
  img: HTMLImageElement | null;
}

/** One pyramid level: a `grid`×`grid` array of tiles covering the whole bbox. */
interface SatLevel {
  grid: number;
  tiles: SatTile[];
}

/**
 * Satellite imagery cache, keyed by trackId. Because every lap on a track covers
 * the same ground, the imagery is fetched once per track and reused for all
 * comparisons and reference-lap changes. `geo` is the fixed geographic extent
 * the tiles were fetched for (reprojected per build to stay aligned).
 */
const satCache = new Map<string, { geo: MapData['geo']; levels: SatLevel[]; loading: boolean }>();

/** Choose the imagery pyramid level to display for the given view zoom. */
function satLevelForScale(scale: number): number {
  if (scale < 1.5) return 0;
  if (scale < 3) return 1;
  if (scale < 6) return 2;
  return SAT_MAX_LEVEL;
}

/** True once every tile in every pyramid level has a loaded image. */
function pyramidComplete(levels: SatLevel[]): boolean {
  return levels.length > 0 && levels.every((l) => l.tiles.every((t) => t.img));
}


export function TrackMap(): React.ReactElement {
  const referenceLap = useWorkspaceStore((s) => s.referenceLap);
  const mapColourMode = useWorkspaceStore((s) => s.mapColourMode);
  const setMapColourMode = useWorkspaceStore((s) => s.setMapColourMode);
  const trackId = useWorkspaceStore((s) => s.trackId);
  const tracks = useWorkspaceStore((s) => s.tracks);
  const setZoom = useWorkspaceStore((s) => s.setZoom);
  const zoom = useWorkspaceStore((s) => s.zoom);
  const comparisonLaps = useWorkspaceStore((s) => s.comparisonLaps);
  const hiddenLaps = useWorkspaceStore((s) => s.hiddenLaps);
  const showCornerNames = useWorkspaceStore((s) => s.showCornerNames);
  const setShowCornerNames = useWorkspaceStore((s) => s.setShowCornerNames);
  const mapCollapsed = useLayoutStore((s) => !!s.collapsed.map);
  const toggleCollapsed = useLayoutStore((s) => s.toggleCollapsed);
  const playing = usePlaybackStore((s) => s.playing);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pathRef = useRef<MapData | null>(null);
  /** Latest hidden lapKeys, read from the stable draw loop. */
  const hiddenRef = useRef<string[]>(hiddenLaps);
  hiddenRef.current = hiddenLaps;
  const viewRef = useRef<View>({ scale: 1, tx: 0, ty: 0 });
  /** Base fit mapping (fit-only, before the pan/zoom view transform). */
  const fitRef = useRef<{ sx: (x: number) => number; sy: (y: number) => number; w: number; h: number } | null>(null);
  /** Satellite imagery pyramid (index = level); reused per track from cache. */
  const satRef = useRef<SatLevel[]>([]);
  const drawRef = useRef<() => void>(() => {});
  const [ready, setReady] = useState(false);
  const [satEnabled, setSatEnabled] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);

  // Number of sectors: from the track's sector gates if defined, else three.
  const sectorCount = useMemo(
    () => sectorCountForTrack(trackId ? tracks[trackId] : null),
    [trackId, tracks],
  );

  // Named corners for the current track (GPS-anchored; distance derived).
  const corners = useMemo(
    () => (trackId ? (tracks[trackId]?.corners ?? []) : []),
    [trackId, tracks],
  );

  // User-defined sector gates (GPS line segments). Empty ⇒ equal-distance split.
  const sectors = useMemo(
    () => (trackId ? (tracks[trackId]?.sectors ?? []) : []),
    [trackId, tracks],
  );

  // Load + build path when the reference lap, comparisons or colour mode change.
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    pathRef.current = null;
    viewRef.current = { scale: 1, tx: 0, ty: 0 };
    // Note: satRef is intentionally NOT cleared here. The satellite imagery is
    // cached per track (see satCache) and reused across comparison/reference-lap
    // changes, so it stays on screen instead of disappearing while the path
    // rebuilds.
    if (!referenceLap) return;
    const comps: LapRef[] = comparisonLaps;
    void Promise.all([
      loadSession(referenceLap.sessionId),
      ...comps.map((c) => loadSession(c.sessionId)),
    ]).then(([refSession, ...compSessions]) => {
      if (cancelled) return;
      const comparisons = comps.map((c, i) => ({
        session: compSessions[i],
        lapNumber: c.lapNumber,
        colour: lapColour(i + 1),
        key: lapKey(c),
      }));
      const data = buildMap(refSession, referenceLap.lapNumber, lapKey(referenceLap), comparisons, mapColourMode, trackId);
      pathRef.current = data;
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [referenceLap, comparisonLaps, mapColourMode, trackId]);

  // Build + preload a satellite imagery pyramid for the current track, cached
  // per track so it loads exactly once. Level 0 is a single image over the whole
  // track; deeper levels tile the bbox into finer grids fetched at near-max
  // resolution so zooming stays sharp. Levels load coarse-first so the map is
  // usable immediately and sharpens progressively; the API proxy picks the
  // provider (Azure/Esri).
  useEffect(() => {
    if (!ready || !trackId) return;
    const data = pathRef.current;
    if (!data) return;
    const tid = trackId;

    // Build (or reuse) the cached pyramid for this track. The imagery covers the
    // same ground for every lap, so it's fetched once per track and reused for
    // all comparisons / reference-lap changes.
    let entry = satCache.get(tid);
    if (!entry) {
      const g = data.satGeo;
      const levels: SatLevel[] = [];
      for (let L = 0; L <= SAT_MAX_LEVEL; L++) {
        const grid = 1 << L;
        const tiles: SatTile[] = [];
        for (let ty = 0; ty < grid; ty++) {
          for (let tx = 0; tx < grid; tx++) {
            tiles.push({
              u0: tx / grid,
              u1: (tx + 1) / grid,
              v0: ty / grid,
              v1: (ty + 1) / grid,
              img: null,
            });
          }
        }
        levels.push({ grid, tiles });
      }
      entry = { geo: { ...g }, levels, loading: false };
      satCache.set(tid, entry);
    }
    satRef.current = entry.levels;
    drawRef.current();

    // Kick off (or resume) loading whenever the pyramid isn't fully loaded. The
    // loader runs to completion INDEPENDENT of this effect's lifecycle: a
    // re-render re-runs this effect, and previously its cleanup set `cancelled`
    // which both skipped the streamed-in images and aborted the level loop —
    // leaving a blank pyramid cached forever (the intermittent black-map bug).
    // Now the shared cached tiles are always populated, a per-entry `loading`
    // guard prevents duplicate concurrent loaders, and any tile that failed
    // (Esri occasionally 500s) is retried on the next re-render since it stays
    // null and the pyramid is still "incomplete".
    if (!entry.loading && !pyramidComplete(entry.levels)) {
      entry.loading = true;
      const active = entry;
      const g = active.geo;
      const lonSpan = g.maxLon - g.minLon;
      const latSpan = g.maxLat - g.minLat;
      const loadTile = (tile: SatTile): Promise<void> =>
        new Promise((resolve) => {
          if (tile.img) {
            resolve();
            return;
          }
          const url = satelliteUrl({
            minLon: g.minLon + tile.u0 * lonSpan,
            maxLon: g.minLon + tile.u1 * lonSpan,
            minLat: g.minLat + tile.v0 * latSpan,
            maxLat: g.minLat + tile.v1 * latSpan,
          });
          const img = new Image();
          img.onload = () => {
            tile.img = img;
            drawRef.current();
            resolve();
          };
          img.onerror = () => resolve();
          img.src = url;
        });

      void (async () => {
        // Preload one whole level at a time (coarse → fine) so the map is usable
        // immediately and sharpens progressively.
        for (const level of active.levels) {
          await Promise.all(level.tiles.map(loadTile));
        }
        active.loading = false;
      })();
    }
  }, [ready, trackId]);

  // Draw the base line + satellite + cursor marker, plus wire interactions.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let raf = 0;
    const draw = (): void => {
      const data = pathRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      if (!data) return;

      const { bounds } = data;
      const pad = 12;
      const spanX = bounds.maxX - bounds.minX || 1;
      const spanY = bounds.maxY - bounds.minY || 1;
      const fitScale = Math.min((w - pad * 2) / spanX, (h - pad * 2) / spanY);
      const ox = pad + (w - pad * 2 - spanX * fitScale) / 2;
      const oy = pad + (h - pad * 2 - spanY * fitScale) / 2;
      // Base fit mapping (screen y grows downward; invert projected y for north up).
      const fx = (x: number): number => ox + (x - bounds.minX) * fitScale;
      const fy = (y: number): number => h - (oy + (y - bounds.minY) * fitScale);
      fitRef.current = { sx: fx, sy: fy, w, h };

      // Apply the pan/zoom view transform on top of the fit.
      const view = viewRef.current;
      ctx.translate(view.tx, view.ty);
      ctx.scale(view.scale, view.scale);
      // Screen-space line widths so strokes stay crisp at any zoom.
      const lw = (px: number): number => px / view.scale;

      // Satellite background: draw the coarse base image over the whole track,
      // then progressively sharper tiles for the current zoom on top. Loaded
      // tiles only — any not-yet-loaded tile simply shows the coarser image
      // beneath it, so there are never blank gaps while imagery streams in.
      const levels = satEnabled ? satRef.current : [];
      const hasSat = levels.length > 0 && !!levels[0].tiles[0]?.img;
      if (hasSat) {
        const sb = data.satBounds;
        const dx = fx(sb.minX);
        const dy = fy(sb.maxY);
        const dW = (sb.maxX - sb.minX) * fitScale;
        const dH = (sb.maxY - sb.minY) * fitScale;
        ctx.globalAlpha = 0.9;
        const base = levels[0].tiles[0].img;
        if (base) ctx.drawImage(base, dx, dy, dW, dH);
        const displayLevel = satLevelForScale(view.scale);
        for (let L = 1; L <= displayLevel && L < levels.length; L++) {
          for (const tile of levels[L].tiles) {
            if (!tile.img) continue;
            // v grows south→north but screen y grows top→bottom, so the tile's
            // north edge (v1) maps to the smaller (upper) screen y.
            const left = dx + tile.u0 * dW;
            const top = dy + (1 - tile.v1) * dH;
            ctx.drawImage(tile.img, left, top, (tile.u1 - tile.u0) * dW, (tile.v1 - tile.v0) * dH);
          }
        }
        ctx.globalAlpha = 1;
        // Subtle dark wash so the coloured racing line stays legible.
        ctx.fillStyle = 'rgba(6,10,15,0.22)';
        ctx.fillRect(dx, dy, dW, dH);
      }

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const hidden = hiddenRef.current;
      const drawn = data.laps.filter((lp) => !hidden.includes(lp.key));
      const multi = drawn.length > 1;
      // Draw each lap: a solid identity-coloured outline casing, then the
      // accel/brake-coloured core on top so both the lap identity and the
      // driving zones are visible and comparable.
      drawn.forEach((lp) => {
        // Identity outline casing. Thinner when several laps overlap so each
        // car's line through a corner stays distinguishable.
        ctx.strokeStyle = multi ? lp.outline : 'rgba(0,0,0,0.55)';
        ctx.lineWidth = lw(multi ? 3 : 6);
        ctx.beginPath();
        ctx.moveTo(fx(lp.pts[0][0]), fy(lp.pts[0][1]));
        for (let i = 1; i < lp.pts.length; i++) ctx.lineTo(fx(lp.pts[i][0]), fy(lp.pts[i][1]));
        ctx.stroke();

        // Accel/brake-coloured core.
        ctx.lineWidth = lw(multi ? 1.8 : hasSat ? 4 : 3);
        for (let i = 1; i < lp.pts.length; i++) {
          ctx.strokeStyle = colourFor(mapColourMode, lp.colourValues[i], data.range);
          ctx.beginPath();
          ctx.moveTo(fx(lp.pts[i - 1][0]), fy(lp.pts[i - 1][1]));
          ctx.lineTo(fx(lp.pts[i][0]), fy(lp.pts[i][1]));
          ctx.stroke();
        }
      });

      // Sector-start gates across the reference racing line (S/F, then S2, S3…),
      // matching the sector zoom buttons below the map. When the track has
      // user-defined GPS gates we draw those at their real positions; otherwise
      // we fall back to an equal-distance split on the reference lap.
      const refLap = data.laps[0];
      if (refLap) {
        if (sectors.length > 0) {
          // Start/finish tick at the lap boundary (distance 0).
          const sf = perpendicularGate(refLap.pts, 0);
          drawGateLine(ctx, sf.a, sf.b, 'S/F', true, fx, fy, lw);
          const proj = (g: [number, number]): [number, number] =>
            projectLocal(g, data.centre);
          sectors.forEach((gate, i) => {
            drawGateLine(ctx, proj(gate.a), proj(gate.b), `S${i + 2}`, false, fx, fy, lw);
          });
        } else {
          drawSectorGates(ctx, refLap, sectorCount, fx, fy, lw, fitScale);
        }
      }

      // Named corners on the track (toggleable). GPS-anchored: the apex marker
      // sits at its fixed physical location regardless of the reference lap, and
      // each name label sits just outside the track at its saved position (or a
      // default pushed radially outward), linked to the apex by a connector so it
      // never overlaps the racing line. Font stays screen-constant (lw).
      if (showCornerNames && corners.length > 0 && refLap && refLap.lapDistance > 0) {
        ctx.save();
        ctx.font = `${lw(11)}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const centroid = centroidOf(refLap.pts);
        for (const corner of corners) {
          const anchor: [number, number] = corner.apex
            ? projectLocal(corner.apex, data.centre)
            : refLap.pts[nearestByDistance(refLap.distance, corner.distance)];
          const label: [number, number] = corner.label
            ? projectLocal(corner.label, data.centre)
            : outwardLabelPos(anchor, centroid);
          const ax = fx(anchor[0]);
          const ay = fy(anchor[1]);
          const lx = fx(label[0]);
          const ly = fy(label[1]);
          // Connector apex → label.
          ctx.setLineDash([lw(3), lw(3)]);
          ctx.strokeStyle = 'rgba(127,227,255,0.55)';
          ctx.lineWidth = lw(1);
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.lineTo(lx, ly);
          ctx.stroke();
          ctx.setLineDash([]);
          // Apex marker.
          ctx.fillStyle = '#7fe3ff';
          ctx.beginPath();
          ctx.arc(ax, ay, lw(3), 0, Math.PI * 2);
          ctx.fill();
          // Dark outline behind the label keeps it legible over any imagery.
          ctx.lineWidth = lw(3);
          ctx.strokeStyle = 'rgba(0,0,0,0.85)';
          ctx.strokeText(corner.name, lx, ly);
          ctx.fillStyle = '#dff6ff';
          ctx.fillText(corner.name, lx, ly);
        }
        ctx.restore();
      }

      // Cursor marker at the active distance, on every lap. Only drawn when the
      // shared cursor is driven by a genuine hover (map, graph, or timeline), so
      // it overrides playback when you hover but doesn't render a second, jerky
      // set of markers driven by the playback clock (which also moves the shared
      // cursor to scrub the graphs).
      const pb = usePlaybackStore.getState();
      const playbackActive = pb.playing || pb.elapsed > 0;
      const cursor = useCursorStore.getState();
      const active = cursor.active;
      if (active != null && cursor.source === 'hover') {
        drawn.forEach((lp) => {
          const idx = nearestByDistance(lp.distance, active);
          const p = lp.pts[idx];
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = multi ? lp.outline : '#000';
          ctx.lineWidth = lw(2);
          ctx.beginPath();
          ctx.arc(fx(p[0]), fy(p[1]), 5 / view.scale, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        });
      }

      // Playback markers: each lap at its own position for the shared elapsed
      // time, so the cars separate as their pace differs.
      if (playbackActive) {
        drawn.forEach((lp) => {
          if (lp.time.length === 0) return;
          const idx = nearestByDistance(lp.time, pb.elapsed);
          const p = lp.pts[idx];
          const px = fx(p[0]);
          const py = fy(p[1]);
          const r = 7 / view.scale;
          ctx.fillStyle = lp.outline;
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = lw(2.5);
          ctx.beginPath();
          ctx.arc(px, py, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        });
      }
    };

    drawRef.current = draw;
    draw();

    // Redraw on cursor changes via store subscription.
    const unsub = useCursorStore.subscribe(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    });
    // Redraw as the playback clock advances.
    const unsubPlay = usePlaybackStore.subscribe(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    });
    const ro = new ResizeObserver(() => draw());
    ro.observe(canvas);

    // --- Pan & zoom interaction ------------------------------------------
    const zoomAt = (mx: number, my: number, factor: number): void => {
      const v = viewRef.current;
      const scale = Math.min(40, Math.max(1, v.scale * factor));
      const f = scale / v.scale;
      viewRef.current = { scale, tx: mx - f * (mx - v.tx), ty: my - f * (my - v.ty) };
      draw();
    };
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.15 : 1 / 1.15);
    };
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const onDown = (e: MouseEvent): void => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.style.cursor = 'grabbing';
    };
    const onMove = (e: MouseEvent): void => {
      if (!dragging) return;
      const v = viewRef.current;
      viewRef.current = { ...v, tx: v.tx + (e.clientX - lastX), ty: v.ty + (e.clientY - lastY) };
      lastX = e.clientX;
      lastY = e.clientY;
      draw();
    };
    const onUp = (): void => {
      dragging = false;
      canvas.style.cursor = 'grab';
    };
    // Hovering the map moves the shared cursor to the nearest point on the
    // reference racing line, so the graphs track along with the pointer.
    const setActive = useCursorStore.getState().setActive;
    const onHover = (e: MouseEvent): void => {
      if (dragging) return;
      const data = pathRef.current;
      const fit = fitRef.current;
      if (!data || !fit || data.laps.length === 0) return;
      const rect = canvas.getBoundingClientRect();
      const v = viewRef.current;
      // Undo the pan/zoom view transform to get fit-space screen coordinates.
      const fxCoord = (e.clientX - rect.left - v.tx) / v.scale;
      const fyCoord = (e.clientY - rect.top - v.ty) / v.scale;
      const ref = data.laps[0];
      let best = 0;
      let bestD = Infinity;
      for (let i = 0; i < ref.pts.length; i++) {
        const dx = fit.sx(ref.pts[i][0]) - fxCoord;
        const dy = fit.sy(ref.pts[i][1]) - fyCoord;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD) {
          bestD = d2;
          best = i;
        }
      }
      setActive(ref.distance[best]);
    };
    const onLeave = (): void => {
      setActive(null);
    };

    // --- Touch: one-finger drag pans, two-finger pinch zooms (mobile) -----
    // Reuses the same viewRef transform + zoomAt as the mouse/wheel path, so
    // the satellite pyramid stays sharp (unlike a CSS visual zoom).
    let touchMode: 'none' | 'pan' | 'pinch' = 'none';
    let tLastX = 0;
    let tLastY = 0;
    let tPinchDist = 0;
    let tMidX = 0;
    let tMidY = 0;
    const midpoint = (t: TouchList): { x: number; y: number } => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (t[0].clientX + t[1].clientX) / 2 - rect.left,
        y: (t[0].clientY + t[1].clientY) / 2 - rect.top,
      };
    };
    const spread = (t: TouchList): number =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onTouchStart = (e: TouchEvent): void => {
      if (e.touches.length >= 2) {
        touchMode = 'pinch';
        tPinchDist = spread(e.touches);
        const m = midpoint(e.touches);
        tMidX = m.x;
        tMidY = m.y;
      } else if (e.touches.length === 1) {
        touchMode = 'pan';
        tLastX = e.touches[0].clientX;
        tLastY = e.touches[0].clientY;
      }
    };
    const onTouchMove = (e: TouchEvent): void => {
      if (touchMode === 'pan' && e.touches.length === 1) {
        e.preventDefault();
        const v = viewRef.current;
        viewRef.current = {
          ...v,
          tx: v.tx + (e.touches[0].clientX - tLastX),
          ty: v.ty + (e.touches[0].clientY - tLastY),
        };
        tLastX = e.touches[0].clientX;
        tLastY = e.touches[0].clientY;
        draw();
      } else if (touchMode === 'pinch' && e.touches.length >= 2) {
        e.preventDefault();
        const dist = spread(e.touches);
        const m = midpoint(e.touches);
        if (tPinchDist > 0) zoomAt(m.x, m.y, dist / tPinchDist);
        // Pan along with the pinch midpoint so the gesture also drags the map.
        const v = viewRef.current;
        viewRef.current = { ...v, tx: v.tx + (m.x - tMidX), ty: v.ty + (m.y - tMidY) };
        tPinchDist = dist;
        tMidX = m.x;
        tMidY = m.y;
        draw();
      }
    };
    const onTouchEnd = (e: TouchEvent): void => {
      if (e.touches.length === 0) {
        touchMode = 'none';
      } else if (e.touches.length === 1) {
        // Dropped from two fingers to one: continue as a pan.
        touchMode = 'pan';
        tLastX = e.touches[0].clientX;
        tLastY = e.touches[0].clientY;
      }
    };

    canvas.style.cursor = 'grab';
    canvas.style.touchAction = 'none';
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onHover);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd, { passive: true });
    canvas.addEventListener('touchcancel', onTouchEnd, { passive: true });
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      unsub();
      unsubPlay();
      ro.disconnect();
      cancelAnimationFrame(raf);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onHover);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [ready, mapColourMode, satEnabled, sectorCount, sectors, showCornerNames, corners]);  // Redraw (without refetching imagery) when laps are hidden/shown.
  useEffect(() => {
    drawRef.current();
  }, [hiddenLaps]);

  // While the clock is running, drive the map with its own animation frame loop
  // so the playback markers advance smoothly. (A store subscription alone can
  // miss frames because the cursor and playback subscriptions share one rAF
  // handle and cancel each other.)
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const loop = (): void => {
      drawRef.current();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  // Zoom buttons operate around the canvas centre.
  const zoomButton = (factor: number): void => {
    const fit = fitRef.current;
    const v = viewRef.current;
    const cx = (fit?.w ?? 0) / 2;
    const cy = (fit?.h ?? 0) / 2;
    const scale = Math.min(40, Math.max(1, v.scale * factor));
    const f = scale / v.scale;
    viewRef.current = { scale, tx: cx - f * (cx - v.tx), ty: cy - f * (cy - v.ty) };
    drawRef.current();
  };

  const resetView = (): void => {
    viewRef.current = { scale: 1, tx: 0, ty: 0 };
    drawRef.current();
    setZoom(null);
  };

  /** Fit the map view to a distance range and zoom the graphs to match. */
  const focusRange = (d0: number, d1: number): void => {
    const data = pathRef.current;
    const fit = fitRef.current;
    if (!data || !fit || data.laps.length === 0) return;
    const ref = data.laps[0];
    let fx0 = Infinity;
    let fy0 = Infinity;
    let fx1 = -Infinity;
    let fy1 = -Infinity;
    for (let i = 0; i < ref.distance.length; i++) {
      if (ref.distance[i] < d0 || ref.distance[i] > d1) continue;
      const sx = fit.sx(ref.pts[i][0]);
      const sy = fit.sy(ref.pts[i][1]);
      if (sx < fx0) fx0 = sx;
      if (sx > fx1) fx1 = sx;
      if (sy < fy0) fy0 = sy;
      if (sy > fy1) fy1 = sy;
    }
    if (!Number.isFinite(fx0)) return;
    const bw = Math.max(fx1 - fx0, 1);
    const bh = Math.max(fy1 - fy0, 1);
    const scale = Math.min(40, 0.8 * Math.min(fit.w / bw, fit.h / bh));
    viewRef.current = {
      scale,
      tx: fit.w / 2 - (scale * (fx0 + fx1)) / 2,
      ty: fit.h / 2 - (scale * (fy0 + fy1)) / 2,
    };
    drawRef.current();
    setZoom([d0, d1]);
  };

  const lapDistance = pathRef.current?.laps[0]?.lapDistance ?? 0;

  return (
    <div className={`map-panel${mapCollapsed ? ' collapsed' : ''}`}>
      <div className="toolbar">
        <button
          type="button"
          className="panel-title collapse-head"
          style={{ padding: 0 }}
          onClick={() => toggleCollapsed('map')}
        >
          <span className="chev">{mapCollapsed ? '▸' : '▾'}</span>
          Track map
        </button>
        <div className="spacer" style={{ flex: 1 }} />
        {!mapCollapsed && (
          <>
            <label className="small muted" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={satEnabled} onChange={(e) => setSatEnabled(e.target.checked)} />
              Satellite
            </label>
            <label className="small muted" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="checkbox"
                checked={showCornerNames}
                onChange={(e) => setShowCornerNames(e.target.checked)}
              />
              Corner names
            </label>
            <button className="small edit-track-btn" title="Edit track corners & sectors" onClick={() => setEditorOpen(true)}>
              Edit Track
            </button>
            <select value={mapColourMode} onChange={(e) => setMapColourMode(e.target.value as MapColourMode)}>
              <option value="longitudinal_g">Accel / Brake (long. G)</option>
              <option value="brake">Brake %</option>
              <option value="speed">Speed</option>
            </select>
          </>
        )}
      </div>
      <div className="map-wrap">
        <canvas ref={canvasRef} className="track" />
        {referenceLap && (
          <div className="map-controls">
            <button title="Zoom in" onClick={() => zoomButton(1.3)}>
              ＋
            </button>
            <button title="Zoom out" onClick={() => zoomButton(1 / 1.3)}>
              －
            </button>
            <button title="Fit whole lap" onClick={resetView}>
              ⤢
            </button>
          </div>
        )}
        {!referenceLap && (
          <div className="muted" style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
            Select a reference lap
          </div>
        )}
      </div>
      {referenceLap && lapDistance > 0 && (
        <div className="sector-bar">
          <button className={zoom ? '' : 'primary'} onClick={resetView} title="Show the whole lap">
            Full lap
          </button>
          {Array.from({ length: sectorCount }, (_, i) => {
            const d0 = (lapDistance * i) / sectorCount;
            const d1 = (lapDistance * (i + 1)) / sectorCount;
            return (
              <button
                key={i}
                onClick={() => focusRange(d0, d1)}
                title={`Zoom to sector ${i + 1} (${Math.round(d0)}–${Math.round(d1)} m)`}
              >
                S{i + 1}
              </button>
            );
          })}
        </div>
      )}
      <MapLegend mode={mapColourMode} range={pathRef.current?.range ?? [0, 1]} />
      {editorOpen && <TrackEditor onClose={() => setEditorOpen(false)} />}
    </div>
  );
}

function MapLegend(props: { mode: MapColourMode; range: [number, number] }): React.ReactElement {
  const items =
    props.mode === 'longitudinal_g'
      ? [
          { c: 'rgb(255,70,70)', l: 'Braking' },
          { c: 'rgb(120,130,140)', l: 'Coasting' },
          { c: 'rgb(34,240,110)', l: 'Accelerating' },
        ]
      : props.mode === 'brake'
        ? [
            { c: 'rgb(120,130,140)', l: '0%' },
            { c: 'rgb(255,70,70)', l: '100%' },
          ]
        : [
            { c: 'rgb(74,163,255)', l: 'Slow' },
            { c: 'rgb(251,191,36)', l: 'Mid' },
            { c: 'rgb(255,70,70)', l: 'Fast' },
          ];
  return (
    <div className="legend">
      {items.map((it) => (
        <span key={it.l} className="item">
          <span className="swatch" style={{ background: it.c, height: 6 }} />
          {it.l}
        </span>
      ))}
    </div>
  );
}
